import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { UserPlus, Trash2, Search, Loader2 } from 'lucide-react';
import { useAuthContext } from '@/contexts/auth-context';
import {
  useAssignStudent,
  useRemoveStudentFromTeacher,
  useSearchTeachers,
} from '@/hooks/use-admin';
import { toast } from 'sonner';
import { toToastMessage } from '@/lib/api-error';
import { formatDate } from '@/lib/utils';
import type { StudentTeacherItem } from '@/types/dashboard';
import type { StudentSearchItem } from '@/types/admin';

interface StudentTeachersCardProps {
  studentId: string;
  teachers: StudentTeacherItem[];
}

function formatTeacherName(t: StudentSearchItem): string {
  const name = [t.first_name, t.last_name].filter(Boolean).join(' ');
  return name || t.username || t.email || t.id.slice(0, 8);
}

function formatTeacherDisplayName(t: StudentTeacherItem): string {
  const name = [t.first_name, t.last_name].filter(Boolean).join(' ');
  return name || t.email || t.id.slice(0, 8);
}

export function StudentTeachersCard({ studentId, teachers }: StudentTeachersCardProps) {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const assignMutation = useAssignStudent();
  const removeMutation = useRemoveStudentFromTeacher();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchResults, isLoading: isSearching } = useSearchTeachers(debouncedQuery);

  const linkedIds = new Set(teachers.map((t) => t.id));
  const candidates = (searchResults ?? []).filter((t) => !linkedIds.has(t.id));

  const handleAssign = (teacherID: string) => {
    assignMutation.mutate(
      { teacherID, studentID: studentId },
      {
        onSuccess: () => {
          toast.success('Teacher added');
          setDialogOpen(false);
          setQuery('');
        },
        onError: (err) => toast.error(toToastMessage(err, 'Failed to add teacher')),
      },
    );
  };

  const handleRemove = (teacherID: string, teacherName: string) => {
    if (!confirm(`Remove ${teacherName} from this student?`)) return;
    removeMutation.mutate(
      { teacherID, studentID: studentId },
      {
        onSuccess: () => toast.success('Teacher removed'),
        onError: (err) => toast.error(toToastMessage(err, 'Failed to remove teacher')),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Teachers ({teachers.length})</CardTitle>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger render={<Button size="sm" variant="outline" />}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add Teacher
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Assign Teacher</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="teacher-search">Search teachers</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="teacher-search"
                      placeholder="Name, email, or username..."
                      value={query}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border">
                  {query.length < 2 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Type at least 2 characters to search.
                    </p>
                  ) : isSearching ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : candidates.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      No teachers found.
                    </p>
                  ) : (
                    candidates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleAssign(t.id)}
                        disabled={assignMutation.isPending}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                      >
                        <div>
                          <p className="font-medium">{formatTeacherName(t)}</p>
                          {t.email && <p className="text-xs text-muted-foreground">{t.email}</p>}
                        </div>
                        <UserPlus className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {teachers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No teachers assigned to this student.
          </p>
        ) : (
          <div className="space-y-2">
            {teachers.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <button
                  type="button"
                  onClick={() => isAdmin && navigate(`/teachers/${t.id}`)}
                  className={`flex-1 text-left ${isAdmin ? 'cursor-pointer hover:underline' : ''}`}
                >
                  <p className="font-medium">{formatTeacherDisplayName(t)}</p>
                  {t.email && <p className="text-sm text-muted-foreground">{t.email}</p>}
                  <p className="text-xs text-muted-foreground">
                    Linked {formatDate(t.linked_at)}
                  </p>
                </button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(t.id, formatTeacherDisplayName(t))}
                    disabled={removeMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
